/*eslint-disable*/
import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { clearToken } from '../store/slices/authSlice';

const AdminContainer = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
  padding: 30px;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const AdminHeader = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }

  h1 {
    color: #333;
    font-size: 32px;
    font-weight: 600;
    margin: 0;
  }
`;

const AdminActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  padding: 10px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  width: 300px;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const RefreshButton = styled.button`
  padding: 10px 20px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s;

  &:hover:not(:disabled) {
    background: #5568d3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const AdminContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const MembersTableContainer = styled.div`
  overflow-x: auto;
`;

const MembersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;

  thead {
    background: #f8f9fa;
  }

  th {
    padding: 16px;
    text-align: left;
    font-weight: 600;
    color: #555;
    border-bottom: 2px solid #e0e0e0;
    font-size: 14px;

    @media (max-width: 768px) {
      padding: 10px;
    }
  }

  td {
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
    color: #333;
    font-size: 14px;

    @media (max-width: 768px) {
      padding: 10px;
      font-size: 12px;
    }
  }

  tbody tr:hover {
    background: #f8f9fa;
  }
`;

const NoData = styled.td`
  text-align: center;
  color: #999;
  padding: 40px !important;
  font-style: italic;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.active ? '#d4edda' : '#f8d7da'};
  color: ${props => props.active ? '#155724' : '#721c24'};
`;

const MembersInfo = styled.div`
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
  color: #666;
  font-size: 14px;

  strong {
    color: #333;
    font-size: 16px;
  }
`;

const SortableTh = styled.th`
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  &:hover {
    background: #eef0f3;
  }
`;

function Admin() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const navigate = useNavigate();
  
  // Redux store에서 token 가져오기
  const authState = useSelector((state) => state.auth);
  const token = authState.token;

  // 필터링할 이메일 목록
  const filteredEmails = [
    'vossam99@naver.com',
    '0502tjkim@gmail.com',
    'luckystrike_03@naver.com',
    'peanut.kitchen@gmail.com',
    'rhrhrh87@daum.net'
  ];

  // 토큰이 없으면 로그인 페이지로 리다이렉트
  useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [token, navigate]);

  // API로 회원 리스트 가져오기
  const fetchMembers = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/client`, {
        headers: {
          'access-token': token
        }
      });
      
      // console.log('API 응답 데이터:', res.data);
      
      // API 응답 데이터 처리 (배열인 경우)
      if (Array.isArray(res.data)) {
        // 특정 이메일 필터링
        const filteredData = res.data.filter(member => 
          !filteredEmails.includes(member.email)
        );
        setMembers(filteredData);
      } else if (res.data?.data && Array.isArray(res.data.data)) {
        // 응답이 { data: [...] } 형태인 경우
        const filteredData = res.data.data.filter(member => 
          !filteredEmails.includes(member.email)
        );
        setMembers(filteredData);
      } else {
        setMembers([]);
      }
      
    } catch (error) {
      console.error('회원 리스트 조회 실패:', error);
      if (error?.response?.status === 401) {
        navigate('/login');
      } else {
        alert(error?.response?.data?.detail || '회원 리스트를 불러오는데 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchMembers();
    }
  }, [token]);

  // 날짜 포맷팅 함수
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // 상태 한글 변환
  const getStatusText = (status) => {
    if (status === 'approved') return '승인';
    if (status === 'pending') return '대기';
    if (status === 'rejected') return '거부';
    return status;
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const filteredMembers = members
    .filter(member =>
      (member.name && member.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (member.email && member.email.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      let aVal = a[sortKey] ?? '';
      let bVal = b[sortKey] ?? '';
      if (sortKey === 'created_at' || sortKey === 'latest_login_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (sortKey === 'status') {
        aVal = getStatusText(aVal);
        bVal = getStatusText(bVal);
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const handleRefresh = () => {
    fetchMembers();
  };

  return (
    <AdminContainer>
      <AdminHeader>
        <h1>회원 관리</h1>
        <AdminActions>
          <SearchInput
            type="text"
            placeholder="이름 또는 이메일로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <RefreshButton onClick={handleRefresh} disabled={loading}>
            {loading ? '새로고침 중...' : '새로고침'}
          </RefreshButton>
        </AdminActions>
      </AdminHeader>

      <AdminContent>
        <MembersTableContainer>
          <MembersTable>
            <thead>
              <tr>
                <th>ID</th>
                <SortableTh onClick={() => handleSort('name')}>이름{sortIcon('name')}</SortableTh>
                <SortableTh onClick={() => handleSort('email')}>이메일{sortIcon('email')}</SortableTh>
                <SortableTh onClick={() => handleSort('created_at')}>가입일{sortIcon('created_at')}</SortableTh>
                <SortableTh onClick={() => handleSort('latest_login_at')}>최종 접속일{sortIcon('latest_login_at')}</SortableTh>
                <SortableTh onClick={() => handleSort('status')}>상태{sortIcon('status')}</SortableTh>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.length === 0 ? (
                <tr>
                  <NoData colSpan="6">
                    {searchTerm ? '검색 결과가 없습니다.' : '회원 데이터가 없습니다.'}
                  </NoData>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <tr key={member.id}>
                    <td>{member.id}</td>
                    <td>{member.name || '-'}</td>
                    <td>{member.email || '-'}</td>
                    <td>{formatDate(member.created_at)}</td>
                    <td>{formatDate(member.latest_login_at)}</td>
                    <td>
                      <StatusBadge active={member.status === 'approved'}>
                        {getStatusText(member.status)}
                      </StatusBadge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </MembersTable>
        </MembersTableContainer>

        <MembersInfo>
          <p>총 회원 수: <strong>{filteredMembers.length}명</strong></p>
        </MembersInfo>
      </AdminContent>
    </AdminContainer>
  );
}

export default Admin;
